import { useCallback } from "react";

import { useHistory } from "react-router";
import { CLOUD_FLOW_CREATION_STATUS } from "@doitintl/cmp-models";
import { Box, Container, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";

import { cloudflowTexts } from "../../../assets/texts";
import { cmpBaseColors } from "../../../cmpBaseColors";
import { useDarkThemeCheck } from "../../../Components/hooks/useDarkThemeCheck";
import { CircularProgressLoader } from "../../../Components/Loader";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { type CustomerPageId } from "../../../Navigation/config/pages";
import mixpanel from "../../../utils/mixpanel";
import BlueprintsRow from "../CloudflowBlueprints/BlueprintsRow";
import CloudflowBrowser from "../CloudflowBrowser/CloudflowBrowser";
import { initialNodes } from "../CloudflowBuilder/hooks/consts";
import { CLOUDFLOW_CONTENT_MAX_WIDTH } from "../consts";
import {
  useCloudflowExecutions,
  useCloudflows,
  useCreateCloudflow,
  useCreateCloudflowFromBlueprint,
  usePromotedBlueprints,
} from "../hooks";
import NavatticSection from "../NavatticSection";
import { type CloudflowBlueprint } from "../types";
import { CloudflowFAQ, CloudFlowOverview } from "./CloudflowDefaultState";
import { PublishCloudflowPrompt } from "./PublishCloudflowPrompt";

type DashboardProps = {
  pageId: CustomerPageId;
};

const Dashboard = ({ pageId }: DashboardProps) => {
  const history = useHistory();
  const { customer } = useCustomerContext();
  const isPresentationMode = isCustomerInPresentationMode(customer);

  const {
    currentUser: { email },
  } = useAuthContext({ mustHaveUser: true });

  const { cloudflows, cloudflowsLoading } = useCloudflows();
  const { cloudflowExecutions, cloudflowExecutionsLoading } = useCloudflowExecutions();
  const { blueprints, blueprintsLoading } = usePromotedBlueprints();
  const [createCloudflow, loading] = useCreateCloudflow();
  const [createCloudflowFromBlueprint, creatingCloudflowFromBlueprint] = useCreateCloudflowFromBlueprint();

  const handleCreateCloudflow = useCallback(
    async (cloudflowName: string) => {
      const result = await createCloudflow(customer.id, {
        name: cloudflowName,
        status: CLOUD_FLOW_CREATION_STATUS.DRAFT,
        firstNodeId: initialNodes[0].id,
        nodes: initialNodes,
        collaborators: [{ email, role: "owner" }],
        type: isPresentationMode ? "demo" : "custom",
      });

      const cloudflowId = result?.data.id;

      if (cloudflowId) {
        history.push(`/customers/${customer.id}/operate/cloudflow/edit/${cloudflowId}`);
      }
    },
    [email, createCloudflow, customer.id, history, isPresentationMode]
  );

  const onCreateCloudflow = useCallback(async () => {
    await handleCreateCloudflow("New Cloudflow");
  }, [handleCreateCloudflow]);

  const onClickViewAll = useCallback(() => {
    history.push(`/customers/${customer.id}/operate/cloudflow/blueprints/`);
  }, [history, customer.id]);

  const onBlueprintClick = useCallback(
    async (blueprint: CloudflowBlueprint) => {
      if (isPresentationMode) {
        history.push(`/customers/${customer.id}/operate/cloudflow/blueprint/${blueprint.id}`);
        return;
      }
      const res = await createCloudflowFromBlueprint(customer.id, blueprint);

      if (res) {
        mixpanel.track("cloudflow.create");
        history.push(`/customers/${customer.id}/operate/cloudflow/edit/${res.id}`);
      }
    },
    [isPresentationMode, createCloudflowFromBlueprint, customer.id, history]
  );

  const isDarkMode = useDarkThemeCheck();

  const shouldRenderDefaultState =
    !cloudflowsLoading &&
    !cloudflowExecutionsLoading &&
    !cloudflows?.length &&
    (!cloudflowExecutions?.length || isPresentationMode);

  const hasPublishedCloudflows = Boolean(
    cloudflows?.length &&
      cloudflows?.some((cloudflow) => cloudflow.data.status === CLOUD_FLOW_CREATION_STATUS.PUBLISHED)
  );

  const shouldShowPublishPrompt = !cloudflowsLoading && !hasPublishedCloudflows && !isPresentationMode;

  if (cloudflowsLoading || cloudflowExecutionsLoading || blueprintsLoading) {
    return <CircularProgressLoader />;
  }
  return (
    <>
      {pageId === "cloudflow" && (
        <>
          {shouldRenderDefaultState ? (
            <>
              <Container maxWidth="lg" sx={{ p: 4 }}>
                <CloudFlowOverview onCreateCloudflow={onCreateCloudflow} createCloudFlowLoading={loading} />
                <Stack px={1} maxWidth={{ xs: CLOUDFLOW_CONTENT_MAX_WIDTH, sm: "100%" }}>
                  <BlueprintsRow
                    blueprintsLoading={blueprintsLoading}
                    blueprints={blueprints}
                    onBlueprintClick={onBlueprintClick}
                    onClickViewAll={onClickViewAll}
                    disabled={creatingCloudflowFromBlueprint}
                  />
                </Stack>
              </Container>
              <NavatticSection />
              <Stack
                mx={-2}
                justifyContent="center"
                sx={{
                  backgroundColor: isDarkMode ? cmpBaseColors.backgroundDark : cmpBaseColors.backgroundLight,
                }}
              >
                <Container maxWidth="lg" sx={{ px: 2 }}>
                  <CloudflowFAQ />
                </Container>
              </Stack>
            </>
          ) : (
            <Box
              sx={{
                pt: 1,
              }}
            >
              <Grid container spacing={1}>
                <Grid
                  sx={{
                    pt: 2,
                  }}
                  size={12}
                >
                  <Stack
                    sx={{
                      gap: 1,
                    }}
                  >
                    <Typography variant="h1">{cloudflowTexts.CLOUDFLOW}</Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {cloudflowTexts.DASHBOARD_SUBHEADER}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
              {shouldShowPublishPrompt && (
                <PublishCloudflowPrompt onGetStarted={onCreateCloudflow} createCloudFlowLoading={loading} />
              )}
              <Box
                sx={{
                  mb: 3,
                }}
              />
              <BlueprintsRow
                blueprintsLoading={blueprintsLoading}
                blueprints={blueprints}
                onBlueprintClick={onBlueprintClick}
                onClickViewAll={onClickViewAll}
                disabled={creatingCloudflowFromBlueprint}
              />
              <Box
                sx={{
                  mb: 3,
                }}
              />
              <CloudflowBrowser
                cloudflows={cloudflows}
                loading={cloudflowsLoading}
                createCloudFlowLoading={loading}
                onCreateCloudflow={onCreateCloudflow}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Dashboard;
